import * as React from 'react';
import type { RouteObject } from 'react-router-dom';
import { redirect } from 'react-router-dom';
import { PrivateRoute } from 'containers/PrivateRoute';
import { LicencedPage } from 'routing/licencedPage';
import routes from 'routes';
import { LoginPage } from 'pages/Login';
import { SignUpPage, SignupSuccessPage, ValidateUser } from 'pages/sign-up';
import { ResetPasswordPage } from 'pages/forgot-password';
import { PasswordEmailSuccessPage } from 'pages/forgot-password/email-success';
import { TermsOfUse } from 'pages/terms-of-use';
import { PrivacyPolicy } from 'pages/privacy-policy';
import { AccessibilityStatement } from 'pages/accessibility-statement';
import { AccessibilityPage } from 'pages/accessibility';
import { HomePage } from 'pages/home';
import { AddReturnPage } from 'pages/returns';
import { TifdMappingPage } from 'pages/returns/tifd-mapping';
import { UsersPage } from 'pages/users';
import { AccountPage } from 'pages/account';
import { VatReportPage, TifdReportPage } from 'pages/vat';
import { BillingPage } from 'pages/billing';
import { VIESPage } from 'pages/vies';
import { OrganisationsPage } from 'pages/organisations';
import { OrganisationPage } from 'pages/organisations/organisation';
import { ReportingPage } from 'pages/reporting';
import { PicPage } from 'pages/account/pic-page';
import { CustomersSuppliersPage } from 'pages/vies/customers-suppliers';
import { ContactPage } from 'pages/vies/contact';
import { PricingPage } from 'pages/billing/pricing-page';
import { CheckoutPage } from 'pages/billing/checkout-page';
import { NotFoundPage } from 'pages/not-found';
import { SubmissionsPage } from 'pages/submissions';
import { PlannerPage } from 'pages/planner';
import { OneDriveCallbackPage } from 'components/OrganisationDetails/OneDrive/one-drive-callback';
import { isDesignGalleryEnabled } from 'utils/design-gallery-enabled';
import {
  DesignSystemButtonsPage,
  DesignSystemDataDisplayPage,
  DesignSystemFeedbackPage,
  DesignSystemFormsPage,
  DesignSystemHubPage,
  DesignSystemLayoutPage,
  DesignSystemOverlaysPage,
  DesignSystemPatternsPage,
  DesignSystemTablesPage,
  DesignSystemAiPage,
  DesignSystemProvidersPage,
  DesignSystemListsCardsPage,
  DesignSystemIntegrationsPage,
  DesignSystemDataRetrievalPage,
  DesignSystemIconsPage,
  DesignSystemUtilitiesPage,
  DesignSystemFeatureDotsPage,
  DesignSystemNavigationPage,
  DesignSystemWorkflowsPage,
  DesignSystemSubmissionsPage,
  DesignSystemDashboardPage,
  DesignSystemVatReturnsPage,
  DesignSystemMappingPage,
  DesignSystemPermissionsPage,
  DesignSystemAuthPage,
  DesignSystemBillingPage,
  DesignSystemAntdThemePage,
} from 'components/dev/design-gallery/lazy-gallery-pages';

type PageComponent = React.ComponentType<Record<string, unknown>>;

const publicRoute = (path: string, Component: PageComponent): RouteObject => ({
  path,
  element: React.createElement(Component),
});

const privateRoute = (path: string, Component: PageComponent): RouteObject => ({
  path,
  element: <PrivateRoute>{React.createElement(Component)}</PrivateRoute>,
});

const licencedPrivateRoute = (
  path: string,
  Component: PageComponent
): RouteObject => ({
  path,
  element: (
    <PrivateRoute>
      <LicencedPage component={Component} />
    </PrivateRoute>
  ),
});

const redirectRoute = (path: string, to: string): RouteObject => ({
  path,
  loader: () => redirect(to),
});

const signupSSORoute = (): RouteObject => ({
  path: routes.signUp.signupSSO,
  loader: ({ request }) => {
    const url = new URL(request.url);
    return redirect(`${routes.signUp.signupForm}${url.search}`);
  },
});

const tempRedirectorRoute = (): RouteObject => ({
  path: routes.tempRedirector,
  loader: ({ params, request }) => {
    const url = new URL(request.url);
    const route = params.route?.replace(/<slash>/g, '/');
    return redirect(route ? route + url.search : routes.main);
  },
});

const designGalleryRoutes: RouteObject[] = isDesignGalleryEnabled
  ? [
      privateRoute(routes.dev.designSystem.root, DesignSystemHubPage),
      privateRoute(routes.dev.designSystem.buttons, DesignSystemButtonsPage),
      privateRoute(routes.dev.designSystem.forms, DesignSystemFormsPage),
      privateRoute(
        routes.dev.designSystem.dataDisplay,
        DesignSystemDataDisplayPage
      ),
      privateRoute(routes.dev.designSystem.feedback, DesignSystemFeedbackPage),
      privateRoute(routes.dev.designSystem.overlays, DesignSystemOverlaysPage),
      privateRoute(routes.dev.designSystem.layout, DesignSystemLayoutPage),
      privateRoute(routes.dev.designSystem.patterns, DesignSystemPatternsPage),
      privateRoute(routes.dev.designSystem.tables, DesignSystemTablesPage),
      privateRoute(routes.dev.designSystem.ai, DesignSystemAiPage),
      privateRoute(
        routes.dev.designSystem.providers,
        DesignSystemProvidersPage
      ),
      privateRoute(
        routes.dev.designSystem.listsCards,
        DesignSystemListsCardsPage
      ),
      privateRoute(
        routes.dev.designSystem.integrations,
        DesignSystemIntegrationsPage
      ),
      privateRoute(
        routes.dev.designSystem.dataRetrieval,
        DesignSystemDataRetrievalPage
      ),
      privateRoute(routes.dev.designSystem.icons, DesignSystemIconsPage),
      privateRoute(
        routes.dev.designSystem.utilities,
        DesignSystemUtilitiesPage
      ),
      privateRoute(
        routes.dev.designSystem.featureDots,
        DesignSystemFeatureDotsPage
      ),
      privateRoute(
        routes.dev.designSystem.navigation,
        DesignSystemNavigationPage
      ),
      privateRoute(
        routes.dev.designSystem.workflows,
        DesignSystemWorkflowsPage
      ),
      privateRoute(
        routes.dev.designSystem.submissions,
        DesignSystemSubmissionsPage
      ),
      privateRoute(
        routes.dev.designSystem.dashboard,
        DesignSystemDashboardPage
      ),
      privateRoute(
        routes.dev.designSystem.vatReturns,
        DesignSystemVatReturnsPage
      ),
      privateRoute(routes.dev.designSystem.mapping, DesignSystemMappingPage),
      privateRoute(
        routes.dev.designSystem.permissions,
        DesignSystemPermissionsPage
      ),
      privateRoute(routes.dev.designSystem.auth, DesignSystemAuthPage),
      privateRoute(routes.dev.designSystem.billing, DesignSystemBillingPage),
      privateRoute(
        routes.dev.designSystem.antdTheme,
        DesignSystemAntdThemePage
      ),
      redirectRoute(routes.dev.buttons, routes.dev.designSystem.buttons),
    ]
  : [];

/** Central route table shared by the browser router and SSR static router. */
export const appRouteObjects: RouteObject[] = [
  publicRoute(routes.oneDriveCallback, OneDriveCallbackPage),
  publicRoute(routes.login, LoginPage),
  tempRedirectorRoute(),
  publicRoute(routes.signUp.signupForm, SignUpPage),
  publicRoute(routes.signUp.validateUser, ValidateUser),
  publicRoute(routes.signUp.signupSuccess, SignupSuccessPage),
  signupSSORoute(),
  publicRoute(routes.resetPassword.request, ResetPasswordPage),
  publicRoute(routes.resetPassword.confirm, ValidateUser),
  publicRoute(routes.resetPassword.emailSent, PasswordEmailSuccessPage),
  publicRoute(routes.termsOfUse, TermsOfUse),
  publicRoute(routes.privacyPolicy, PrivacyPolicy),
  publicRoute(routes.accessibilityStatement, AccessibilityStatement),
  licencedPrivateRoute(routes.main, HomePage),
  redirectRoute('/', routes.main),
  licencedPrivateRoute(routes.submissions, SubmissionsPage),
  licencedPrivateRoute(routes.planner, PlannerPage),
  privateRoute(routes.returns.add, AddReturnPage),
  privateRoute(routes.returns.edit, AddReturnPage),
  privateRoute(routes.returns.tifdAdd, TifdMappingPage),
  privateRoute(routes.returns.tifdEdit, TifdMappingPage),
  licencedPrivateRoute(routes.organisations.all, OrganisationsPage),
  licencedPrivateRoute(routes.organisations.organisation, OrganisationPage),
  licencedPrivateRoute(routes.users, UsersPage),
  licencedPrivateRoute(routes.account.root, AccountPage),
  licencedPrivateRoute(routes.account.details, AccountPage),
  licencedPrivateRoute(routes.account.error, AccountPage),
  licencedPrivateRoute(routes.accessibility, AccessibilityPage),
  licencedPrivateRoute(routes.billing.root, BillingPage),
  privateRoute(routes.pricing.root, PricingPage),
  privateRoute(routes.pricing.checkout, CheckoutPage),
  privateRoute(routes.reporting, ReportingPage),
  licencedPrivateRoute(routes.vies.qualifiedEntities, VIESPage),
  licencedPrivateRoute(
    routes.vies.customersAndSuppliers,
    CustomersSuppliersPage
  ),
  licencedPrivateRoute(routes.vies.contact, ContactPage),
  licencedPrivateRoute(routes.vat.viewReportLink, VatReportPage),
  licencedPrivateRoute(routes.vat.report, VatReportPage),
  licencedPrivateRoute(routes.vat.tifdViewReportLink, TifdReportPage),
  licencedPrivateRoute(routes.vat.tifdReport, TifdReportPage),
  privateRoute(routes.pictureTest, PicPage),
  ...designGalleryRoutes,
  publicRoute(routes.notFound, NotFoundPage),
  redirectRoute(routes.vat.root, routes.main),
  redirectRoute(routes.signUp.root, routes.signUp.signupForm),
  { path: '*', loader: () => redirect(routes.notFound) },
];
